import { useEffect, useState, useMemo } from 'react';
import { checkAdEnabled } from '@service/chapterListService';
import { RootState } from '@store/store';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { STORAGE_KEY } from '@common/values';
import { setAdInfo } from '@/slices/chatChapterSlice';
import {
  useGetAdInfoChapterQuery,
  useGetAdInfoStoryQuery,
  useGetAdInfoUserQuery,
} from '@/operations/queries/novelChapter/__generated__/useGetAdInfo.generated';
import { isMobileDevice } from '@/common/utils';

interface IChapterAd {
  storyId?: number;
  chapterId?: number;
}

const getWatchedAdChapterId = () => {
  return (
    typeof window !== 'undefined' &&
    sessionStorage.getItem(STORAGE_KEY.WATCHED_AD_CHAPTER_ID)
  );
};

const useCheckAdByRemoteConfig = ({ storyId, chapterId }: IChapterAd) => {
  const {
    data: userData,
    error: userError,
    loading: userLoading,
  } = useGetAdInfoUserQuery({
    fetchPolicy: 'network-only',
  });
  const {
    data: chapterData,
    error: chapterError,
    loading: chapterLoading,
  } = useGetAdInfoChapterQuery({
    variables: {
      chapterId: chapterId!,
    },
    skip: !chapterId,
    fetchPolicy: 'no-cache',
  });
  const {
    data: storyData,
    error: storyError,
    loading: storyLoading,
  } = useGetAdInfoStoryQuery({
    variables: {
      storyId: storyId!,
    },
    skip: !storyId,
    fetchPolicy: 'network-only',
  });

  const [isShowAdIcon, setIsShowAdIcon] = useState(false);
  const [isShowAd, setIsShowAd] = useState<boolean | undefined>();
  const dispatch = useAppDispatch();
  const isRestarting = useAppSelector(
    (state: RootState) => state.chapter.isRestarting,
  );

  const adInfoStorage = useAppSelector(
    (state: RootState) => state.chapter.adInfoStorage,
  );

  const [watchedAdChapterId, setWatchedAdChapterId] = useState(
    getWatchedAdChapterId(),
  );

  useEffect(() => {
    const handleChangeStorage = () => {
      setWatchedAdChapterId(getWatchedAdChapterId());
    };

    // sessionStorage 정보가 업데이트되면 리렌더링
    window.addEventListener('storage', handleChangeStorage);
    return () => window.removeEventListener('storage', handleChangeStorage);
  }, []);

  const checkAd = async () => {
    if (!storyId || !chapterId || !chapterData || !storyData) return;

    // remote-config 설정에 따라 광고를 보여주는 스토리인가?
    let isAdEnabled = adInfoStorage[storyId];

    if (isAdEnabled === undefined) {
      isAdEnabled = await checkAdEnabled({ storyId });
      dispatch(setAdInfo({ storyId, isAdEnabled }));
    }
    // 무료인가?
    const isFree =
      chapterData?.getChapter.freedAt !== null ||
      chapterData.getChapter.price === 0 ||
      (chapterData.getChapter.freeDependencyChapterIndex !== null &&
        chapterData.getChapter.freeDependencyPeriod !== null &&
        chapterData.getChapter.remainingTimeToFree === 0 &&
        chapterData.getChapter.freedAt !== null);

    // 전체구매했는가?
    const isAllChapterPurchased =
      chapterData.getChapter.userPurchasedChapter !== null;
    // 통구매 작품이고 통구매했는가?
    const isWholePurchase =
      storyData?.story.purchasable && storyData?.story.purchased;

    // 프리미엄(구독권)유저인가?
    const isPremium = userData?.getUser?.isPremium;
    const userPlayedChapters = storyData.story.lastPlay
      ? storyData.story.lastPlay?.playedChapters
      : [];
    // 최종화 엔딩에 도달했나?
    const hasReachedFinalEnding = userPlayedChapters?.some(
      (chapter) => chapter.ending?.isFinal === true,
    );
    const playingChapterIds = userPlayedChapters?.map(
      (chapter) => chapter.chapterId,
    );
    // 회차를 플레이 중인가?
    const isPlaying = playingChapterIds.some((id) => id === chapterId);
    // 유저가 해당 챕터의 광고를 이미 봤나?
    const isWatchedAdChapter =
      Number(sessionStorage.getItem(STORAGE_KEY.WATCHED_AD_CHAPTER_ID)) ===
      chapterId;

    // showAdIcon state 값 정의
    if (
      (isAdEnabled &&
        isFree &&
        !isAllChapterPurchased &&
        !isWholePurchase &&
        !isPremium &&
        !hasReachedFinalEnding) ||
      (chapterData.getChapter.adsOn &&
        isFree &&
        !isAllChapterPurchased &&
        !isWholePurchase &&
        !isPremium &&
        !hasReachedFinalEnding)
    ) {
      setIsShowAdIcon(true);
    }
    // showAd state 값 정의

    let showAd;

    if (
      (isAdEnabled &&
        isFree &&
        !isAllChapterPurchased &&
        !isWatchedAdChapter &&
        !isWholePurchase &&
        !isPremium) ||
      (chapterData.getChapter.adsOn &&
        isFree &&
        !isAllChapterPurchased &&
        !isWatchedAdChapter &&
        !isWholePurchase &&
        !isPremium)
    ) {
      if (isRestarting) {
        showAd = true;
      } else {
        if (!isPlaying) {
          showAd = true;
        }
      }
    }

    // 모바일 앱 디바이스에서 웹뷰형태로 실행된 경우 광고를 보여주지 않는다.
    if (isMobileDevice()) {
      showAd = false;
    }

    setIsShowAd(showAd ? true : false);
  };

  useMemo(() => {
    checkAd();
  }, [
    storyId,
    chapterId,
    adInfoStorage,
    watchedAdChapterId,
    chapterData,
    storyData,
  ]);

  return {
    isShowAdIcon,
    isShowAd,
    setIsShowAd,
    error: chapterError || storyError || userError,
    loading: chapterLoading || storyLoading || userLoading,
  };
};

export default useCheckAdByRemoteConfig;
